import type {
  UserDetail,
  UserDetailDto,
  UserList,
  UserListDto,
  UserUpdateDto
} from "@src/features/users/types";
import type { AddUserFormData } from "@src/pages/Users/AddUser/types";
import { branchSearchFromDto } from "@src/features/users/lib/branch.ts";
import { rankFromDto } from "@src/features/users/lib/rank.ts";
import {jobTitleFromDto, teamFromDto, titleFromDto} from "@src/features/users";
import {departmentFromDto} from "@src/features/users/lib/department.ts";
import {employmentFromDto} from "@src/features/users/lib/employment.ts";
import {
  type AppLanguage,
  DEFAULT_APP_LANGUAGE,
  getIntlLocaleTag
} from "@src/shared";

function dateToDtoString(date: Date | null): string | undefined {
  if (!date) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function userListFromDto(user: UserListDto): UserList {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    language: user.language,
    isAdmin: user.is_admin,
    manager: user.user_user_manager,
    hr: user.user_user_hr,
    portrait: user.user_portrait,
    jobTitle: user.user_job_title ? jobTitleFromDto(user.user_job_title) : null,
    rank: user.user_rank ? rankFromDto(user.user_rank) : null,
    status: user.status,
    salaryPercentage: user.salary_percentage,
    branch: user.user_branch ? branchSearchFromDto(user.user_branch) : null,
    initials: user.user_initials,
    startDate: user.start_date,
    department: user.user_department,
    team: user.user_team,
    deputy: user.user_user_deputy,
    title: user.user_title,
  };
}

export function userDetailFromDto(user: UserDetailDto): UserDetail {
  const activeSalary = (user.user_salaries || []).find((salary) => salary.is_active);
  const salary = activeSalary ? {
    salaryStartDate: activeSalary.saly_start ? new Date(activeSalary.saly_start) : null,
    salaryPercentage: activeSalary.saly_percentage,
    salaryVacationAllowance: activeSalary.saly_vacation_allowance,
    salaryMonthly: activeSalary.saly_monthly,
    salaryEmployment: activeSalary.saly_emty ? employmentFromDto(activeSalary.saly_emty) : null,
  } : {
    salaryStartDate: null,
    salaryPercentage: null,
    salaryVacationAllowance: null,
    salaryMonthly: null,
    salaryEmployment: null,
  };
  const emergency = [{
    contact: user.user_emergency_contact1,
    relationship: user.user_emergency_relationship1,
    mobile: user.user_emergency_mobile1,
    email: user.user_emergency_email1,
  }]
  if (
    user.user_emergency_contact2
    || user.user_emergency_relationship2
    || user.user_emergency_mobile2
    || user.user_emergency_email2
  ) {
    emergency.push({
      contact: user.user_emergency_contact2,
      relationship: user.user_emergency_relationship2,
      mobile: user.user_emergency_mobile2,
      email: user.user_emergency_email2,
    })
  }
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    initials: user.user_initials,
    language: user.language,
    status: user.status,
    isAdmin: user.is_admin,
    branch: user.user_branch ? branchSearchFromDto(user.user_branch) : null,
    portrait: user.user_portrait,
    rank: user.user_rank ? rankFromDto(user.user_rank) : null,
    jobTitle: user.user_job_title ? jobTitleFromDto(user.user_job_title) : null,
    manager: user.user_user_manager,
    hr: user.user_user_hr,
    title: user.user_title ? titleFromDto(user.user_title) : null,
    team: user.user_team ? teamFromDto(user.user_team) : null,
    department: user.user_department ? departmentFromDto(user.user_department) : null,
    deputy: user.user_user_deputy,
    ...salary,
    signature: user.user_signature,
    birthdate: user.user_birthdate ? new Date(user.user_birthdate) : null,
    mobile: user.user_mobile,
    phone: user.user_phone ?? "",
    emailPrivate: user.user_email_private,
    mobilePrivate: user.user_mobile_private,
    phonePrivate: user.user_phone_private,
    addressLine1: user.user_address_line_1,
    addressLine2: user.user_address_line_2,
    city: user.user_city,
    postcode: user.user_postcode,
    country: user.user_country,
    emergency,
  };
}

export function userUpdateToDto(user: AddUserFormData): UserUpdateDto {
  return {
    email: user.email,
    user_first_name: user.firstName,
    user_family_name: user.familyName,
    user_initials: user.initials,
    language: getIntlLocaleTag((user.language || DEFAULT_APP_LANGUAGE) as AppLanguage) ,
    user_branch: user.branch?.id,
    user_portrait: user.photoFile ?? undefined,
    user_rank: user.rankId || undefined,
    user_job_title: user.jobTitleId || undefined,
    user_user_manager: user.manager?.id,
    user_user_hr: user.hr?.id,
    user_title: user.title || undefined,
    user_team: user.team?.id,
    user_department: user.department?.id,
    user_user_deputy: user.deputy?.id,
    user_signature: user.signature ?? undefined,
    user_birthdate: dateToDtoString(user.birthdate),
    user_mobile: user.mobile || undefined,
    user_phone: user.phone,
    user_email_private: user.emailPrivate || undefined,
    user_mobile_private: user.mobilePrivate || undefined,
    user_phone_private: user.phonePrivate || undefined,
    user_address_line_1: user.addressLine1 || undefined,
    user_address_line_2: user.addressLine2 || undefined,
    user_city: user.city || undefined,
    user_postcode: user.postcode || undefined,
    user_country: user.country || undefined,
    user_emergency_contact1: user.emergency[0]?.contact || undefined,
    user_emergency_relationship1: user.emergency[0]?.relationship || undefined,
    user_emergency_mobile1: user.emergency[0]?.mobile || undefined,
    user_emergency_email1: user.emergency[0]?.email || undefined,
    user_emergency_contact2: user.emergency[1]?.contact || undefined,
    user_emergency_relationship2: user.emergency[1]?.relationship || undefined,
    user_emergency_mobile2: user.emergency[1]?.mobile || undefined,
    user_emergency_email2: user.emergency[1]?.email || undefined,
    salary_id: user.salaryId,
    salary_saly_start: dateToDtoString(user.salaryStartDate),
    salary_saly_percentage: user.salaryPercentage ?? undefined,
    salary_saly_monthly: user.salaryMonthly ?? undefined,
    salary_saly_vacation_allowance: user.salaryVacationAllowance ?? undefined,
    salary_saly_emty: user.salaryEmployment?.id,
  };
}
